// THE PAGE REGISTRY — what the shell can show, and who may see it.
//
// Pages are DATA, not a switch statement, for two reasons that matter here:
//
//  1. The nav and the router read the same list, so a page cannot exist in one and not
//     the other. A hand-written nav beside a hand-written router is two lists that drift,
//     and the drift shows up as a link to nothing or a page nobody can reach.
//  2. Access is a FIELD on the page, so "which pages may this session see" is a filter
//     over data rather than a condition repeated per page — and a new page cannot be
//     added without stating what it requires.
//
// ---------------------------------------------------------------------------
// HIDING A NAV LINK IS NOT ACCESS CONTROL
// ---------------------------------------------------------------------------
// `visiblePages()` decides the NAV. `canOpen()` decides whether a page may RENDER, and
// the router calls it again for whatever page id it is asked for — including one typed
// into the address bar, restored from a bookmark, or left in the URL after a role
// changed. The two are deliberately separate functions over the same field: a reviewer
// can see that removing the link does not remove the page.
//
// ---------------------------------------------------------------------------
// SCOPE NOTE — the four "Explore the demo" pages
// ---------------------------------------------------------------------------
// The brief names the group and one member ("Security tour") but not the other three.
// These four are this repo's reading of what the demo actually has to show, and they are
// data — renaming, reordering or replacing one is an edit to this array and nothing else.
// Flagged rather than presented as settled.

import { can, type Capabilities, type CapabilityName } from '../auth/capabilities';

/** The nav groups, in display order. */
/**
 * The nav groups, in display order.
 *
 * "Assistant" is CONSTANT across brands — it names the domain, not the agent — so it is
 * deliberately NOT derived from the brand profile, unlike the agent name. Pinned by a
 * test so a future "helpful" parameterisation is caught.
 */
export const PAGE_GROUPS = ['Assistant', 'Explore the demo', 'Settings'] as const;
export type PageGroup = (typeof PAGE_GROUPS)[number];

export interface PageDef {
  /** Stable id; also the URL hash (`#/<id>`). */
  readonly id: string;
  readonly label: string;
  readonly group: PageGroup;
  /**
   * One line under the heading. May contain `{agent}`, substituted at render from the
   * brand profile — the agent name is brand data for this SDK (§1 carve-out), so it is
   * never a literal in the registry.
   */
  readonly blurb: string;
  /**
   * The capability a session must hold to OPEN this page, or null for "any signed-in
   * session".
   *
   * `null` is explicit rather than an omitted field: an optional property would read as
   * `undefined` for a page whose author forgot it, and "forgot to state it" would become
   * "open to everyone" — the same fall-through-to-permissive shape the capability
   * resolver exists to prevent. Here the type forces a decision.
   */
  readonly requires: CapabilityName | null;
}

export const PAGES: readonly PageDef[] = Object.freeze([
  {
    id: 'overview',
    label: 'Overview',
    group: 'Assistant',
    blurb: 'What this demo is, and which published artifact it embeds.',
    requires: null,
  },
  {
    id: 'embed',
    label: 'Launcher',
    group: 'Assistant',
    blurb: 'The real launcher component, mounted as a consuming app mounts it.',
    requires: 'launchAnna',
  },
  {
    id: 'context',
    label: 'Caller context',
    group: 'Assistant',
    blurb: 'The prep-session handshake that hands {agent} rich context.',
    requires: 'launchAnna',
  },
  {
    id: 'hosts',
    label: 'Host integration',
    group: 'Explore the demo',
    blurb: 'The real wire-up, in two independent host applications.',
    requires: 'launchAnna',
  },
  {
    id: 'target',
    label: 'Target & SDK',
    group: 'Explore the demo',
    blurb: 'Which {agent} the launcher opens, probed from the SDK itself.',
    requires: null,
  },
  {
    id: 'security',
    label: 'Security tour',
    group: 'Explore the demo',
    blurb: 'What each tier can and cannot do, and why the default is least privilege.',
    requires: null,
  },
  {
    id: 'identity',
    label: 'Your session',
    group: 'Explore the demo',
    blurb: 'The AuthSession this demo resolved, field by field.',
    requires: null,
  },
  {
    id: 'sandbox',
    label: 'Demo sandbox',
    group: 'Explore the demo',
    blurb: 'Reset the demo to its initial state. A write, so elevated only.',
    requires: null,
  },
  {
    id: 'settings',
    label: 'Settings',
    group: 'Settings',
    blurb: 'Target environment and caller context. Writes are elevated only.',
    requires: null,
  },
]);

/** The landing page. Requires nothing, so every signed-in session can reach it. */
export const DEFAULT_PAGE_ID = 'overview';

export function findPage(id: string): PageDef | null {
  return PAGES.find((p) => p.id === id) ?? null;
}

/**
 * May this session OPEN this page?
 *
 * The single check the router calls. A page with `requires: null` is open to any
 * signed-in session — reaching this function at all already means a resolved session,
 * because the gate runs first.
 */
export function canOpen(caps: Capabilities, page: PageDef): boolean {
  return page.requires === null ? true : can(caps, page.requires);
}

/** The pages to show in the nav, in registry order. */
export function visiblePages(caps: Capabilities): readonly PageDef[] {
  return PAGES.filter((p) => canOpen(caps, p));
}

/** Visible pages grouped for rendering, skipping groups that end up empty. */
export function navGroups(caps: Capabilities): Array<{ group: PageGroup; pages: PageDef[] }> {
  return PAGE_GROUPS.map((group) => ({
    group,
    pages: visiblePages(caps).filter((p) => p.group === group),
  })).filter((g) => g.pages.length > 0);
}

/**
 * Resolve a requested page id to the page that should actually render.
 *
 * FAILS CLOSED TO THE DEFAULT PAGE, never to the requested one. An unknown id, or one the
 * session may not open, resolves to `overview` — which requires nothing, so this cannot
 * recurse into an unreachable page.
 *
 * Returns the reason too, so the UI can say "that page is not available to you" instead
 * of silently showing something else, which reads as a broken link.
 */
export type PageResolution =
  | { kind: 'ok'; page: PageDef }
  | { kind: 'denied'; requested: PageDef; fallback: PageDef }
  | { kind: 'unknown'; requestedId: string; fallback: PageDef };

export function resolvePage(caps: Capabilities, requestedId: string | null): PageResolution {
  const fallback = findPage(DEFAULT_PAGE_ID);
  // The default page is a constant in this module, so this cannot be missing unless the
  // registry is edited inconsistently — in which case failing loudly here is right.
  if (!fallback) throw new Error(`page registry has no "${DEFAULT_PAGE_ID}" page`);

  const id = (requestedId ?? '').trim();
  if (id === '') return { kind: 'ok', page: fallback };

  const page = findPage(id);
  if (!page) return { kind: 'unknown', requestedId: id, fallback };
  if (!canOpen(caps, page)) return { kind: 'denied', requested: page, fallback };
  return { kind: 'ok', page };
}

/** Parse `#/<id>` from a URL hash. Returns null for anything else. */
export function pageIdFromHash(hash: string): string | null {
  const m = /^#\/([A-Za-z0-9_-]+)\/?$/.exec(hash.trim());
  return m ? m[1]! : null;
}

/** The hash for a page id. One definition, so the nav and the router agree. */
export function hashForPage(id: string): string {
  return `#/${id}`;
}
